// Global JavaScript for Kashkaval Catering Theme

// Clean up orphaned upcharge items (upcharges without parent packages)
async function cleanupOrphanedUpcharges() {
  try {
    if (!window.cartManagerInstance) return;
    const cart = await window.cartManagerInstance.getCart();

    // Check if there are any packages in the cart
    const packageHandles = ['party-package-small', 'party-package-large', 'lunch-package', 'cocktail-party-package'];
    const hasPackages = cart.items.some(item => packageHandles.includes(item.handle));

    // If no packages, remove ALL hidden upcharge items
    if (!hasPackages) {
      const upchargeItems = cart.items.filter(item =>
        (item.handle === 'premium-skewer-salmon' || item.handle === 'premium-skewer-octopus' || item.handle === 'mixed-dessert-plate') &&
        item.properties &&
        item.properties['_Hidden'] === 'true'
      );

      // Remove each orphaned upcharge
      let removedAny = false;
      for (const upchargeItem of upchargeItems) {
        const lineIndex = cart.items.findIndex(item => item.key === upchargeItem.key);
        if (lineIndex !== -1) {
          const removeResponse = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              line: lineIndex + 1,
              quantity: 0
            })
          });
          if (removeResponse.ok) removedAny = true;
        }
      }

      // Re-render cart to reflect removed items
      if (removedAny) {
        window.cartManagerInstance.invalidateCache();
        await window.cartManagerInstance.refreshCart();
      }
    }
  } catch (error) {
    console.error('Error cleaning up orphaned upcharges:', error);
  }
}

// Cart functionality
class CartManager {
  constructor() {
    this.cartDrawer = document.getElementById('cart-drawer');
    this.cartCount = document.getElementById('cart-count');
    this.previousCartItems = new Map(); // Track previous cart state for highlighting
    this.requestQueue = []; // Queue for cart API requests
    this.isProcessingQueue = false; // Flag to prevent concurrent processing
    this.lastRequestTime = 0; // Track last request time for rate limiting
    this.minRequestInterval = 1000; // Minimum time between requests (ms) - increased for Shopify rate limits
    this.cachedCart = null; // Cache cart data to reduce API calls
    this.cartCacheTime = 0; // Track when cart was cached
    this.cartCacheMaxAge = 2000; // Cache cart data for 2 seconds
    this._initStaticEventListeners();
    this.init();
  }

  _initStaticEventListeners() {
    // Open cart drawer
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', () => this.openCart());
    });

    // Close cart drawer
    document.querySelectorAll('[data-cart-drawer-close]').forEach(btn => {
      btn.addEventListener('click', () => this.closeCart());
    });

    // Close on overlay click
    const overlay = document.querySelector('.cart-drawer__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.closeCart());
    }
  }

  init() {
    // Cart quantity controls (dynamic elements re-created on each render)
    this.setupQuantityControls();

    // Remove item
    document.querySelectorAll('[data-cart-remove]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        if (btn.disabled) return;

        const key = e.target.dataset.cartRemove;
        const cartItem = document.querySelector(`[data-cart-item="${key}"]`);

        // Add loading state
        if (cartItem) {
          cartItem.classList.add('cart-item--updating');
        }

        btn.disabled = true;
        const originalText = btn.textContent;
        btn.textContent = 'Removing...';

        const success = await this.updateCartItem(key, 0);

        // Revert state on error (on success, cart will refresh and replace the DOM)
        if (!success) {
          if (cartItem) {
            cartItem.classList.remove('cart-item--updating');
          }
          btn.disabled = false;
          btn.textContent = originalText;
        }
      });
    });
  }

  setupQuantityControls() {
    // Increase quantity
    document.querySelectorAll('[data-cart-increase]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        if (btn.disabled) return;

        const key = e.target.dataset.cartIncrease;
        const cartItem = document.querySelector(`[data-cart-item="${key}"]`);
        const input = document.querySelector(`[data-cart-quantity="${key}"]`);
        const oldQty = parseInt(input.value);
        const newQty = oldQty + 1;

        // Add loading state
        if (cartItem) {
          cartItem.classList.add('cart-item--updating');
        }
        btn.disabled = true;

        const success = await this.updateCartItem(key, newQty);

        // Revert state on error
        if (!success) {
          if (cartItem) {
            cartItem.classList.remove('cart-item--updating');
          }
          input.value = oldQty;
          btn.disabled = false;
        }
      });
    });

    // Decrease quantity
    document.querySelectorAll('[data-cart-decrease]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        if (btn.disabled) return;

        const key = e.target.dataset.cartDecrease;
        const cartItem = document.querySelector(`[data-cart-item="${key}"]`);
        const input = document.querySelector(`[data-cart-quantity="${key}"]`);
        const oldQty = parseInt(input.value);
        const newQty = Math.max(0, oldQty - 1);

        // Add loading state
        if (cartItem) {
          cartItem.classList.add('cart-item--updating');
        }
        btn.disabled = true;

        const success = await this.updateCartItem(key, newQty);

        // Revert state on error
        if (!success) {
          if (cartItem) {
            cartItem.classList.remove('cart-item--updating');
          }
          input.value = oldQty;
          btn.disabled = false;
        }
      });
    });

    // Manual quantity change
    document.querySelectorAll('[data-cart-quantity]').forEach(input => {
      input.addEventListener('change', async (e) => {
        if (input.disabled) return;

        const key = e.target.dataset.cartQuantity;
        const cartItem = document.querySelector(`[data-cart-item="${key}"]`);
        const oldQty = parseInt(input.value);
        const newQty = Math.max(0, parseInt(e.target.value));

        // Add loading state
        if (cartItem) {
          cartItem.classList.add('cart-item--updating');
        }
        input.disabled = true;

        const success = await this.updateCartItem(key, newQty);

        // Revert state on error
        if (!success) {
          if (cartItem) {
            cartItem.classList.remove('cart-item--updating');
          }
          input.value = oldQty;
          input.disabled = false;
        }
      });
    });
  }

  async openCart(highlightNewItems = false) {
    await this.refreshCart(highlightNewItems);

    if (this.cartDrawer) {
      this.cartDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
  }

  closeCart() {
    if (this.cartDrawer) {
      this.cartDrawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  }

  // Private method to fetch cart data with caching (no queue)
  async _fetchCartData(forceRefresh = false) {
    const now = Date.now();
    const cacheAge = now - this.cartCacheTime;

    // Return cached cart if it's fresh and not forcing refresh
    if (!forceRefresh && this.cachedCart && cacheAge < this.cartCacheMaxAge) {
      return this.cachedCart;
    }

    // Fetch fresh cart data
    const response = await fetch('/cart.js');
    if (!response.ok) {
      throw new Error(`Failed to fetch cart: ${response.status}`);
    }

    const cart = await response.json();
    this.cachedCart = cart;
    this.cartCacheTime = Date.now();
    return cart;
  }

  // Public method to get cart data through queue
  async getCart(forceRefresh = false) {
    return this.queueRequest(async () => {
      return await this._fetchCartData(forceRefresh);
    });
  }

  // Invalidate cart cache after updates
  invalidateCache() {
    this.cachedCart = null;
    this.cartCacheTime = 0;
  }

  // Add request to queue and process
  queueRequest(requestFn) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ fn: requestFn, resolve, reject });
      this.processQueue();
    });
  }

  // Process queued requests sequentially with rate limiting
  async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.requestQueue.length > 0) {
      const { fn, resolve, reject } = this.requestQueue.shift();

      try {
        // Enforce minimum interval between requests
        const now = Date.now();
        const timeSinceLastRequest = now - this.lastRequestTime;
        if (timeSinceLastRequest < this.minRequestInterval) {
          await new Promise(r => setTimeout(r, this.minRequestInterval - timeSinceLastRequest));
        }

        this.lastRequestTime = Date.now();
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    }

    this.isProcessingQueue = false;
  }

  async updateCartItem(key, quantity) {
    return this.queueRequest(async () => {
      try {
        // First get the cart to find the line number
        const cartResponse = await fetch('/cart.js');

        if (!cartResponse.ok) {
          console.error(`Failed to fetch cart: ${cartResponse.status}`);
          window.showToast('Unable to update cart. Please try again.', 'error');
          return false;
        }

        const cart = await cartResponse.json();

        // Find the line item by key
        const lineIndex = cart.items.findIndex(item => item.key === key);

        if (lineIndex === -1) {
          console.error('Item not found in cart');
          return false;
        }

        const item = cart.items[lineIndex];

        // Check if this is a party package being removed
        const packageHandles = ['party-package-small', 'party-package-large', 'lunch-package', 'cocktail-party-package'];
        const isPackageRemoval = quantity === 0 && packageHandles.includes(item.handle);

        // Update using line number (1-based)
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            line: lineIndex + 1,
            quantity: quantity
          })
        });

        if (!response.ok) {
          console.error(`Failed to update cart: ${response.status}`);
          const errorMessage = response.status === 429 ?
            'Please wait a moment before making more changes.' :
            'Unable to update cart. Please try again.';
          window.showToast(errorMessage, 'error');
          return false;
        }

        // Invalidate cache after successful update
        this.invalidateCache();

        // If removing a party package, also remove associated upcharge items
        if (isPackageRemoval) {
          // Get fresh cart data after the package removal
          const updatedCartResponse = await fetch('/cart.js');
          if (updatedCartResponse.ok) {
            const updatedCart = await updatedCartResponse.json();

            // Find and remove ALL associated items (hidden upcharges + additional skewers)
            const upchargeItems = updatedCart.items.filter(cartItem => {
              if (!cartItem.properties) return false;
              const isHiddenUpcharge = (cartItem.handle === 'premium-skewer-salmon' || cartItem.handle === 'premium-skewer-octopus' || cartItem.handle === 'mixed-dessert-plate') && cartItem.properties['_Hidden'] === 'true';
              const isAdditionalSkewer = cartItem.handle === 'additional-skewers';
              return isHiddenUpcharge || isAdditionalSkewer;
            });

            for (const upchargeItem of upchargeItems) {
              // Find the line index for this upcharge item
              const upchargeLineIndex = updatedCart.items.findIndex(item => item.key === upchargeItem.key);

              if (upchargeLineIndex !== -1) {
                const upchargeResponse = await fetch('/cart/change.js', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    line: upchargeLineIndex + 1,
                    quantity: 0
                  })
                });

                if (!upchargeResponse.ok) {
                  console.error('Failed to remove upcharge item');
                }
              }
            }
          }
        }

        await this._refreshCartInternal();
        return true;
      } catch (error) {
        // Only catch true exceptions (network errors, JSON parse errors, etc.)
        console.error('Error updating cart:', error);
        window.showToast('Connection issue. Please check your internet and try again.', 'error');
        return false;
      }
    });
  }

  async _refreshCartInternal(highlightNewItems = false) {
    try {
      // Fetch cart data (this method is already in queue, so use _fetchCartData directly)
      const cart = await this._fetchCartData(true); // Force refresh to get latest data

      // Store items that should be highlighted (new or quantity increased)
      const itemsToHighlight = new Set();

      if (highlightNewItems) {
        cart.items.forEach(item => {
          const previousQty = this.previousCartItems.get(item.key) || 0;
          if (item.quantity > previousQty) {
            itemsToHighlight.add(item.key);
          }
        });
      }

      // Update previous cart state
      this.previousCartItems.clear();
      cart.items.forEach(item => {
        this.previousCartItems.set(item.key, item.quantity);
      });

      // Update cart count — exclude hidden upcharge items
      if (this.cartCount) {
        const visibleCount = cart.items.reduce((sum, item) => {
          const isHidden = (item.handle === 'premium-skewer-salmon' || item.handle === 'premium-skewer-octopus' || item.handle === 'mixed-dessert-plate') && item.properties && item.properties['_Hidden'];
          return isHidden ? sum : sum + item.quantity;
        }, 0);
        this.cartCount.textContent = visibleCount;
      }

      // Update subtotal
      const subtotal = document.getElementById('cart-drawer-subtotal');
      if (subtotal) {
        subtotal.textContent = Shopify.formatMoney(cart.total_price);
      }

      // Re-render cart items
      const cartItemsContainer = document.getElementById('cart-drawer-items');
      if (!cartItemsContainer) return;

      if (cart.item_count === 0) {
        cartItemsContainer.innerHTML = `
          <div class="cart-drawer__empty">
            <p>Your cart is empty</p>
            <a href="/collections" class="button">Browse Menu</a>
          </div>
        `;
        // Hide footer and order section
        const footer = document.querySelector('.cart-drawer__footer');
        const orderSection = document.querySelector('.cart-order-section');
        if (footer) footer.style.display = 'none';
        if (orderSection) orderSection.style.display = 'none';
      } else {
        const packageHandles = ['party-package-small', 'party-package-large', 'lunch-package', 'cocktail-party-package'];
        const tapasHandles = ['tapas-sampler-6-compartment-container', 'individual-32-oz-tapas'];
        let itemsHTML = '';
        // Two-pass: render packages/regular items first, additional skewers last
        const sortedItems = [
          ...cart.items.filter(i => i.handle !== 'additional-skewers'),
          ...cart.items.filter(i => i.handle === 'additional-skewers')
        ];
        sortedItems.forEach(item => {
          // Skip upcharge line items (they're hidden and auto-calculated)
          if ((item.handle === 'premium-skewer-salmon' || item.handle === 'premium-skewer-octopus' || item.handle === 'mixed-dessert-plate') && item.properties && item.properties['_Hidden']) {
            return;
          }

          const imageUrl = item.image ? Shopify.Image.getSizedImageUrl(item.image, '100x100') : '';
          const isPackage = packageHandles.includes(item.handle);
          const isTapasProduct = tapasHandles.includes(item.handle);
          const packageClass = isPackage ? ' cart-item--package' : '';
          const highlightClass = itemsToHighlight.has(item.key) ? ' cart-item--just-added' : '';

          // Build variant/properties display
          let variantHTML = '';
          if (isTapasProduct && item.properties && item.properties['Tapas Selections']) {
            // Format tapas selections with counts
            const selections = item.properties['Tapas Selections'].split(',').map(s => s.trim());
            const counts = {};
            selections.forEach(tapas => {
              counts[tapas] = (counts[tapas] || 0) + 1;
            });
            const formatted = Object.entries(counts)
              .map(([name, count]) => count > 1 ? `${name} (${count})` : name)
              .join(', ');
            variantHTML = `<p class="cart-item__variant">${formatted}</p>`;
          } else if (isPackage && item.properties) {
            const properties = Object.entries(item.properties).filter(([key, value]) => {
              return value && !key.startsWith('_') && !key.includes('Upcharge');
            });
            if (properties.length > 0) {
              variantHTML = '<div class="cart-item__variant cart-item__properties">';
              properties.forEach(([key, value]) => {
                // Clean up label: remove size info like (Half Tray), (Full Tray), (15 Total), etc.
                let cleanLabel = key
                  .replace(/\s*\(Half Tray\)/gi, '')
                  .replace(/\s*\(Full Tray\)/gi, '')
                  .replace(/\s*\(\d+\s*Total\)/gi, '')
                  .replace(/^Choose\s+(a|an)\s+/i, '')
                  .replace(/^Choose\s+/i, '')
                  .replace(/^Quantity\s+of\s+/i, '')
                  .replace(/^Add On\s*-\s*/i, '')
                  .replace(/\s+with\s+Pita$/i, '')
                  .trim();
                // Strip sub-labels from dropdown-sequence values (e.g. "Salad One: Kale Salad" → "Kale Salad")
                const cleanValue = value.split(', ').map(part => {
                  const colonIdx = part.indexOf(': ');
                  return colonIdx !== -1 ? part.slice(colonIdx + 2) : part;
                }).join(', ');
                variantHTML += `<span class="cart-item__property"><strong>${cleanLabel}:</strong> ${cleanValue}</span>`;
              });
              variantHTML += '</div>';
            }
          } else if (item.variant_title && item.variant_title !== 'Default Title' && item.variant_title !== 'Standard') {
            const variantDisplay = item.variant_title.replace(/1\/2 Tray/g, 'Half Tray');
            variantHTML = `<p class="cart-item__variant">${variantDisplay}</p>`;
          }

          itemsHTML += `
            <div class="cart-item${packageClass}${highlightClass}" data-cart-item="${item.key}">
              ${item.image ? `<div class="cart-item__image"><img src="${imageUrl}" alt="${item.title}" loading="lazy"></div>` : ''}
              <div class="cart-item__details">
                <h3 class="cart-item__title">${item.product_title}</h3>
                ${variantHTML}
                <div class="cart-item__quantity">
                  <button class="cart-item__qty-btn" data-cart-decrease="${item.key}" aria-label="Decrease quantity">-</button>
                  <input type="number" class="cart-item__qty-input" value="${item.quantity}" min="0" data-cart-quantity="${item.key}" aria-label="Quantity">
                  <button class="cart-item__qty-btn" data-cart-increase="${item.key}" aria-label="Increase quantity">+</button>
                </div>
              </div>
              <div class="cart-item__price">
                <p class="cart-item__total">${Shopify.formatMoney(item.final_line_price)}</p>
                <button class="cart-item__remove" data-cart-remove="${item.key}" aria-label="Remove item">Remove</button>
              </div>
            </div>
          `;
        });

        cartItemsContainer.innerHTML = itemsHTML;

        // Show footer and order section
        const footer = document.querySelector('.cart-drawer__footer');
        const orderSection = document.querySelector('.cart-order-section');
        if (footer) footer.style.display = 'block';
        if (orderSection) orderSection.style.display = 'block';

        // Re-attach event listeners
        this.init();
      }

      // Dispatch cart:updated event for other components (like date/time picker)
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      // Only catch true exceptions (network errors, JSON parse errors, etc.)
      console.error('Error refreshing cart:', error);
      window.showToast('Unable to load cart. Please check your connection.', 'error');
    }
  }

  async refreshCart(highlightNewItems = false) {
    return this.queueRequest(async () => {
      await this._refreshCartInternal(highlightNewItems);
    });
  }
}

// Initialize cart manager
document.addEventListener('DOMContentLoaded', async () => {
  // Initialize CartManager first so other components can use it
  window.cartManagerInstance = new CartManager();

  // Refresh cart on page load to populate package properties and cache data
  // This single fetch will be shared with date/time picker and supplies manager
  await window.cartManagerInstance.refreshCart();

  // Clean up orphaned upcharges after cart is loaded
  await cleanupOrphanedUpcharges();
});

// Add to cart form handling
document.addEventListener('submit', async (e) => {
  if (e.target.classList.contains('product-form')) {
    e.preventDefault();

    const form = e.target;
    const submitButton = form.querySelector('button[type="submit"]');

    // Prevent double submission
    if (submitButton && submitButton.disabled) return;

    let originalButtonText = null;
    if (submitButton) {
      submitButton.disabled = true;
      originalButtonText = submitButton.textContent;
      submitButton.textContent = 'Adding...';
    }

    const formData = new FormData(form);

    // Use the cart manager's queue system
    if (window.cartManagerInstance) {
      try {
        await window.cartManagerInstance.queueRequest(async () => {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) {
            throw new Error(`Failed to add to cart: ${response.status}`);
          }

          return await response.json();
        });

        // Invalidate cache after adding item
        window.cartManagerInstance.invalidateCache();

        // Open cart drawer and highlight new item
        await window.cartManagerInstance.openCart(true);

        if (window.showToast) {
          window.showToast('Item added to cart!', 'success', 2000);
        }
      } catch (error) {
        console.error('Error adding to cart:', error);

        // Provide specific error message based on error type
        let errorMessage = 'Unable to add item to cart. Please try again.';
        if (error.message && error.message.includes('Failed to fetch')) {
          errorMessage = 'Connection issue. Please check your internet and try again.';
        } else if (error.message && error.message.includes('429')) {
          errorMessage = 'Please wait a moment before adding more items.';
        }

        window.showToast(errorMessage, 'error');
      } finally {
        if (submitButton && originalButtonText !== null) {
          submitButton.disabled = false;
          submitButton.textContent = originalButtonText;
        }
      }
    }
  }
});

// Shopify helpers
if (typeof Shopify === 'undefined') {
  Shopify = {};
}

// Image helper
Shopify.Image = {
  getSizedImageUrl: function(src, size) {
    if (!src) return '';
    if (size == null) return src;

    if (size === 'master') return src.replace(/http(s)?:/, '');

    const match = src.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?$/i);
    if (match) {
      const prefix = src.split(match[0]);
      const suffix = match[0];
      return (prefix[0] + '_' + size + suffix).replace(/http(s)?:/, '');
    }
    return src;
  }
};

Shopify.formatMoney = function(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /{{\s*(\w+)\s*}}/;
  const formatString = format || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision || 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};
